import { TABS_COMMAND_K_ID, USER_ROLE } from '../global/constants';
import { ProjectModel } from '../mobx/models/project';
import { TaskModel } from '../mobx/models/task';
import { UserModel } from '../mobx/models/user';
import { rootStore } from '../mobx/rootStore';

export type SearchResultItem<T extends 'project' | 'task' | 'user'> = {
  item: T extends 'project' ? ProjectModel : T extends 'task' ? TaskModel : UserModel;
  type: T;
  score: number;
  highlights: { index: number; length: number }[];
};

export const convertQueryToWordsCollection = (query: string, currentTab: number, filterProjectId?: string) => {
  const { projectStore, taskStore, workspaceStore } = rootStore;
  if (!query) {
    // if no query, return the first 15 projects, tasks, and users
    const projects: SearchResultItem<'project'>[] = projectStore.projects.slice(0, 15).map((project) => ({
      item: project,
      type: 'project',
      score: 0,
      highlights: []
    }));

    const allTasks = filterProjectId
      ? taskStore.tasks.filter((task) => task.projectId === filterProjectId)
      : taskStore.tasks;
    const tasks: SearchResultItem<'task'>[] = allTasks.slice(0, 15).map((task) => ({
      item: task,
      type: 'task',
      score: 0,
      highlights: []
    }));

    const users: SearchResultItem<'user'>[] = (workspaceStore.currentWorkspace?.users || [])
      .slice(0, 15)
      .map((user) => ({
        item: user,
        type: 'user',
        score: 0,
        highlights: []
      }));

    return {
      projects,
      tasks,
      users
    };
  }

  const allWords = query
    .toLowerCase()
    .trim()
    .split(' ')
    .filter((word) => !!word);

  const wordsCollection: Record<number, string[]> = {};

  for (let windowSize = 1; windowSize <= allWords.length; windowSize++) {
    wordsCollection[windowSize] = [];

    for (let i = 0; i + windowSize <= allWords.length; i++) {
      const word = allWords.slice(i, i + windowSize).join(' ');

      if (!word) {
        break;
      }

      wordsCollection[windowSize].push(word.trim());
    }
  }

  return searchData(wordsCollection, currentTab, filterProjectId);
};

export const searchData = (wordsCollection: Record<number, string[]>, currentTab: number, filterProjectId?: string) => {
  const { projectStore, taskStore, workspaceStore } = rootStore;
  const currentUser = rootStore.currentUser;
  const isAdminOrOwner = currentUser?.isAdmin || currentUser?.isOwner;

  const projectsList = projectStore.projects;
  const tasksList = filterProjectId
    ? taskStore.tasks.filter((task) => task.projectId === filterProjectId)
    : taskStore.tasks;
  
  // Filter users based on role
  const usersList = (workspaceStore.currentWorkspace?.users || []).filter((user) => {
    // If current user is a client, only show admin/owner users
    if (currentUser?.role === USER_ROLE.CLIENT) {
      return user.isAdmin || user.isOwner;
    }
    // For non-client users, show all users except clients (unless admin/owner)
    if (user.role !== USER_ROLE.CLIENT) return true;
    // Only include client users if current user is admin/owner
    return isAdminOrOwner;
  });

  const searchResults: Record<
    string,
    SearchResultItem<'project'> | SearchResultItem<'task'> | SearchResultItem<'user'>
  > = {};

  for (const [windowSize, words] of Object.entries(wordsCollection)) {
    for (const word of words) {
      // search through projects
      if (currentTab === TABS_COMMAND_K_ID.PROJECTS || currentTab === TABS_COMMAND_K_ID.ALL) {
        for (const project of projectsList) {
          const projectName = project.name.toLowerCase();
          const wordMatches = findMatchPositions(projectName, word);
          if (wordMatches.length <= 0) continue;

          const score = calculateScore(wordMatches.length, windowSize, 5);
          searchResults[project.id] = {
            item: project,
            type: 'project',
            score: (searchResults[project.id]?.score ?? 0) + score,
            highlights: [...(searchResults[project.id]?.highlights ?? []), ...wordMatches]
          };
        }
      }

      // search through tasks
      if (currentTab === TABS_COMMAND_K_ID.TASKS || currentTab === TABS_COMMAND_K_ID.ALL) {
        for (const task of tasksList) {
          const taskName = task.name.toLowerCase();
          const wordMatches = findMatchPositions(taskName, word);
          if (wordMatches.length <= 0) continue;

          const score = calculateScore(wordMatches.length, windowSize, 2);
          searchResults[task.id] = {
            item: task,
            type: 'task',
            score: (searchResults[task.id]?.score ?? 0) + score,
            highlights: [...(searchResults[task.id]?.highlights ?? []), ...wordMatches]
          };
        }
      }

      // search through users
      if (currentTab === TABS_COMMAND_K_ID.PEOPLE || currentTab === TABS_COMMAND_K_ID.ALL) {
        for (const user of usersList) {
          const userName = user.name.toLowerCase();
          const wordMatches = findMatchPositions(userName, word);
          if (wordMatches.length <= 0) continue;

          const score = calculateScore(wordMatches.length, windowSize, 3);
          searchResults[user.id] = {
            item: user,
            type: 'user',
            score: (searchResults[user.id]?.score ?? 0) + score,
            highlights: [...(searchResults[user.id]?.highlights ?? []), ...wordMatches]
          };
        }
      }
    }
  }

  const sortedSearchResults = Object.values(searchResults)
    .sort((a, b) => b.score - a.score)
    .slice(0, 30)
    .map((result) => {
      result.highlights = filterLongestNonOverlapping(result.highlights);
      return result;
    });

  const { projects, tasks, users } = sortedSearchResults.reduce(
    (acc, item) => {
      if (item.type === 'project') {
        acc.projects.push(item);
      } else if (item.type === 'task') {
        acc.tasks.push(item);
      } else if (item.type === 'user') {
        acc.users.push(item);
      }

      return acc;
    },
    {
      projects: [] as SearchResultItem<'project'>[],
      tasks: [] as SearchResultItem<'task'>[],
      users: [] as SearchResultItem<'user'>[]
    }
  );

  return { projects, tasks, users };
};

function findMatchPositions(str: string, substr: string): { index: number; length: number }[] {
  if (!str || !substr) return [];

  const matches: { index: number; length: number }[] = [];
  const pattern = new RegExp(substr, 'gi');
  let match;

  while ((match = pattern.exec(str))) {
    matches.push({ index: match.index, length: substr.length });
  }

  return matches;
}

function calculateScore(wordCount: number, windowSize: number | string, scoreBoost = 1) {
  return scoreBoost * wordCount * Number(windowSize) ** 2;
}

function filterLongestNonOverlapping(highlights: { index: number; length: number }[]) {
  const sorted = [...highlights].sort((a, b) => {
    if (b.length === a.length) return a.index - b.index;
    return b.length - a.length; // longer first
  });

  const result: { index: number; length: number }[] = [];

  for (const current of sorted) {
    const overlaps = result.some(
      (existing) =>
        !(current.index + current.length <= existing.index || current.index >= existing.index + existing.length)
    );

    if (!overlaps) {
      result.push(current);
    }
  }

  // Optional: sort by index for rendering
  return result.sort((a, b) => a.index - b.index);
}
